import { ArrowUpCircle, Package, RefreshCw } from 'lucide-react';
import clsx from 'clsx';

import { AddonsAddonListItem } from '@api/types.gen';

import { ExternalLink } from '@/react/components/ExternalLink';

import { Card } from '@@/primitives/Card';
import { LinkButton } from '@@/LinkButton';
import { Link } from '@@/Link';
import { FallbackImage } from '@@/FallbackImage';
import { DeleteButton } from '@@/buttons/DeleteButton';
import { LoadingButton } from '@@/buttons';

import { AddonStatusBadge } from '../AddonStatusBadge';
import { AddonVersionBadge } from '../AddonVersionBadge';
import { getAddonLifecycleState } from '../addonLifecycleState';
import { useUninstallAddon } from '../queries/useUninstallAddon';
import { buildUpgradeParams } from '../upgradeDeepLink';
import {
  useAddonRestart,
  confirmAddonRestart,
} from '../DetailView/useAddonRestart';
import { useAddonNamespaceExists } from '../DetailView/queries/useAddonRelease';

interface Props {
  addon: AddonsAddonListItem;
  // disabled gates the addon lifecycle actions when Portainer is not running on
  // Kubernetes, mirroring the platform gate that disables the lifecycle API.
  disabled?: boolean;
}

export function AddonCard({ addon, disabled = false }: Props) {
  const {
    isFailed,
    isInstalling,
    isUnknown,
    isUninstalling,
    isInProgress,
    canUninstall,
    notInstalled,
  } = getAddonLifecycleState(addon);
  const showInstall =
    !isInstalling && !isUninstalling && !isUnknown && !addon.enabled;

  const uninstallMutation = useUninstallAddon(addon.id ?? '');
  const restartMutation = useAddonRestart(addon.id ?? '');
  const namespaceExistsQuery = useAddonNamespaceExists(addon.id ?? '');
  // Restart rolls the namespace's deployments. Show at least when the namespace exists and the addon has any status (not uninstalled)
  const showRestart =
    !!namespaceExistsQuery.data && !notInstalled && !isUninstalling;

  // Link to the detail view whenever there's a live release to open
  const isClickable =
    (!!addon.enabled && !isUninstalling) || isInstalling || isUnknown;

  const latestVersion = addon.record?.availableVersions?.[0];

  return (
    <div>
      <Card.Container
        aria-label={addon.displayName}
        className={clsx(
          'h-full',
          isClickable &&
            'relative cursor-pointer transition-colors hover:border-blue-7 hover:bg-gray-2 th-highcontrast:hover:bg-gray-warm-10 th-dark:hover:bg-gray-iron-10'
        )}
      >
        {isClickable && (
          <Link
            to="portainer.addons.addon"
            params={{ id: addon.id }}
            className="absolute inset-0 z-0"
            aria-label={`Open ${addon.displayName} details`}
            data-cy="addon-card-link"
          />
        )}
        <Card.Body className="flex h-full flex-wrap gap-4 overflow-auto">
          <div className="flex flex-1 gap-4">
            <FallbackImage
              fallbackIcon={<Package className="text-4xl" />}
              src={addon.icon}
              alt={addon.displayName}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md object-contain"
            />
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex items-center gap-4">
                <span className="text-sm font-bold text-gray-9 th-highcontrast:text-white th-dark:text-white">
                  {addon.displayName}
                </span>
                <AddonVersionBadge record={addon.record} />
                <AddonStatusBadge
                  lifecycleStatus={addon.lifecycleStatus}
                  lifecycleMessage={addon.lifecycleStatusMessage}
                  healthStatus={addon.healthStatus}
                  healthMessage={addon.healthMessage}
                />
              </div>
              <span className="text-sm text-gray-9 th-highcontrast:text-white th-dark:text-white">
                {addon.description}
              </span>
            </div>
          </div>
          <div className="relative z-10 ml-auto mt-auto flex items-end gap-2">
            {addon.enabled && (
              <>
                {/*
                  TODO(R8S-1150): when lifecycleStatus is "installed" with a
                  lifecycleStatusMessage (the last upgrade failed and rolled
                  back), add a "Retry upgrade" action here.
                  TODO(R8S-1139): when healthStatus is "unhealthy", add a
                  "Troubleshoot" action linking to the addon's logs/events.
                */}
                {addon.record?.upgradeAvailable &&
                  latestVersion &&
                  !isInProgress && (
                    <LinkButton
                      to="portainer.addons.addon"
                      params={buildUpgradeParams(addon.id)}
                      color="primary"
                      size="medium"
                      icon={ArrowUpCircle}
                      title={`Upgrade to v${latestVersion}`}
                      data-cy="addon-card-upgrade-button"
                    >
                      Upgrade
                    </LinkButton>
                  )}
                <ExternalLink
                  to={addon.path || ''}
                  className="btn btn-secondary items-center gap-1"
                  data-cy="addon-launch-link"
                >
                  Launch
                </ExternalLink>
              </>
            )}
            {showRestart && (
              <LoadingButton
                color="secondary"
                size="medium"
                icon={RefreshCw}
                onClick={handleRestart}
                disabled={disabled}
                isLoading={restartMutation.isLoading}
                loadingText="Restarting..."
                data-cy="addon-card-restart-button"
              >
                Restart
              </LoadingButton>
            )}
            {showInstall && (
              // A failed first install has no live release, so it falls here:
              // the install route doubles as the retry.
              <LinkButton
                to="portainer.addons.install"
                params={{ id: addon.id }}
                color="primary"
                size="medium"
                disabled={disabled}
                title={
                  disabled ? 'Add-ons require a Kubernetes deployment' : ''
                }
                data-cy="addon-install-button"
              >
                {getInstallButtonLabel()}
              </LinkButton>
            )}
            {canUninstall && (
              <DeleteButton
                confirmMessage={`Uninstall ${addon.displayName}? This will remove its deployment.`}
                onConfirmed={() => uninstallMutation.mutate()}
                disabled={disabled || isInProgress}
                isLoading={isUninstalling || uninstallMutation.isLoading}
                loadingText="Loading..."
                size="medium"
                data-cy="addon-uninstall-button"
              >
                Uninstall
              </DeleteButton>
            )}
          </div>
        </Card.Body>
      </Card.Container>
    </div>
  );

  function getInstallButtonLabel() {
    if (disabled) return 'Unavailable';
    return isFailed ? 'Retry install' : 'Install';
  }

  async function handleRestart() {
    if (await confirmAddonRestart(addon.displayName ?? 'Add-on')) {
      restartMutation.mutate();
    }
  }
}
